import { useCallback, useEffect, useRef, useState } from "react";
import { Button, Modal, Space, Tag, Typography } from "antd";
import {
  CopyOutlined,
  DeleteOutlined,
  DownloadOutlined,
} from "@ant-design/icons";
import { streamContainerLogs } from "../Home/Utils/GetAllInstancesAPI";

export default function ContainerLogsModal({
  open,
  onClose,
  containerId,
  containerName,
}) {
  const [lines, setLines] = useState([]);
  const [streaming, setStreaming] = useState(false);
  const mountedRef = useRef(null);
  const boxRef = useRef(null);
  const autoScrollRef = useRef(true);

  const addLine = useCallback((line) => {
    setLines((prev) => [...prev, line]);
  }, []);

  useEffect(() => {
    if (!open || !containerId) return;
    setLines([]);
    setStreaming(true);
    autoScrollRef.current = true;

    mountedRef.current = new AbortController();
    const currentCtrl = mountedRef.current;

    const start = async () => {
      try {
        const res = await streamContainerLogs(containerId, currentCtrl.signal);
        const reader = res.body?.getReader();
        if (!reader) throw new Error("No stream reader");

        const decoder = new TextDecoder("utf-8");
        let buffer = "";

        while (true) {
          const { done, value } = await reader.read();
          if (done) {
            addLine("--- Log stream ended ---");
            break;
          }
          buffer += decoder.decode(value, { stream: true });
          const parts = buffer.split(/\r?\n/);
          buffer = parts.pop() || "";
          for (const raw of parts) {
            const trimmed = raw.trim();
            if (!trimmed) continue;
            if (trimmed.startsWith("data:")) {
              const payload = trimmed.slice(5).trim();
              if (payload) addLine(payload);
            } else if (
              !trimmed.startsWith("id:") &&
              !trimmed.startsWith("event:") &&
              !trimmed.startsWith("retry:")
            ) {
              addLine(trimmed);
            }
          }
        }
      } catch (err) {
        if (currentCtrl.signal.aborted) return;
        addLine(
          err.name === "AbortError"
            ? "--- Log stream stopped ---"
            : `Error: ${err.message}`,
        );
      } finally {
        if (currentCtrl === mountedRef.current) {
          setStreaming(false);
        }
      }
    };

    start();
    return () => currentCtrl.abort();
  }, [open, containerId, addLine]);

  useEffect(() => {
    if (autoScrollRef.current && boxRef.current) {
      boxRef.current.scrollTop = boxRef.current.scrollHeight;
    }
  }, [lines]);

  const onScroll = () => {
    const el = boxRef.current;
    if (!el) return;
    const atBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 50;
    autoScrollRef.current = atBottom;
  };

  const copy = () => navigator.clipboard.writeText(lines.join("\n"));
  const clear = () => setLines([]);
  const download = () => {
    const blob = new Blob([lines.join("\n")], { type: "text/plain" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `logs-${containerName || containerId?.slice(0, 12)}.log`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <Modal
      open={open}
      onCancel={onClose}
      width="85vw"
      title={
        <div className="logs-modal-header">
          <div className="logs-modal-title-row">
            <Typography.Text strong>Container Logs</Typography.Text>
            {containerName && <Tag>{containerName}</Tag>}
          </div>
          <Tag color={streaming ? "green" : "red"}>
            {streaming ? "LIVE" : "STOPPED"}
          </Tag>
        </div>
      }
      footer={null}
    >
      <Space className="logs-modal-actions" wrap>
        <Button
          icon={<DownloadOutlined />}
          onClick={download}
          disabled={!lines.length}
        >
          Download
        </Button>
        <Button icon={<CopyOutlined />} onClick={copy} disabled={!lines.length}>
          Copy
        </Button>
        <Button
          danger
          icon={<DeleteOutlined />}
          onClick={clear}
          disabled={!lines.length}
        >
          Clear
        </Button>
      </Space>

      <div
        ref={boxRef}
        onScroll={onScroll}
        style={{
          height: "70vh",
          overflow: "auto",
          background: "#0b1020",
          color: "#e2e8f0",
          padding: 16,
          borderRadius: 8,
          fontFamily:
            "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace",
          fontSize: 13,
        }}
      >
        {!lines.length && streaming && (
          <div style={{ opacity: 0.6 }}>Waiting for container output…</div>
        )}
        {lines.map((line, i) => (
          <div key={i} style={{ whiteSpace: "pre-wrap" }}>
            {line}
          </div>
        ))}
      </div>
    </Modal>
  );
}
